// Drawer «Crear enlace compartido» de Administración · Enlaces (T-zhjj,
// handoff §9 / prototipo SecEnlaces). Sobre el Drawer compartido, con
// los campos que el backend acepta hoy: las observaciones abiertas a
// incluir (el servidor rechaza mezclar tiendas y acepta hasta
// MAX_POR_ENLACE), para quién (informativo) y vencimiento. El nombre
// interno y el selector «Qué incluye» del handoff quedan fuera hasta que
// existan `internal_name` y `content_kind`: con una sola opción (una
// observación específica) el select no ofrecía elección, así que se
// oculta en vez de simularse. Al crear copia la URL al portapapeles —la
// lista no la vuelve a entregar— y si el portapapeles falla deja el
// enlace visible para copiarlo a mano, como ShareLinkDrawer.

import { useEffect, useId, useRef, useState } from "react";
import type { ShareBundleCreateResponse } from "@bitacorapro/api-types";
import { CopyLinkField } from "../../../components/CopyLinkField";
import { Drawer } from "../../../components/Drawer";
import { PageSkeleton } from "../../../components/PageSkeleton";
import { Skel } from "../../../components/Skel";
import { useFacilityDashboard, useRecipe } from "../../../lib/api/queries";
import { isFacilityDashboardEnabled } from "../../../lib/capabilities";
import { focusHeading } from "../../../lib/useDialogFocus";
import { ADMIN_TOAST_OPTS, useToast } from "../../../lib/useToast";
import { fmtDate } from "../../../lib/util";
import {
  MAX_POR_ENLACE,
  VENCE_EN_DEFAULT,
  VENCE_EN_DIAS,
  errorCrearEnlace,
  excedeTope,
  expiresAtFrom,
  filtrarOpciones,
  opcionesAbiertas,
  tiendasEnSeleccion,
  type VenceEnDias,
} from "./model";
import { useCrearEnlace } from "./queries";

// Igual que StorePicker: bajo este largo el buscador es ruido.
const SEARCH_THRESHOLD = 8;
// Anchos de las filas del esqueleto (mismos que StorePickerSkel).
const SKEL_WIDTHS = ["52%", "38%", "60%", "44%"] as const;

async function copiarAlPortapapeles(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function CrearEnlaceDrawer({ onClose }: { onClose: () => void }) {
  const { data: recipe } = useRecipe();
  const dashboard = useFacilityDashboard(isFacilityDashboardEnabled(recipe));
  const crear = useCrearEnlace();
  const toast = useToast();
  const ids = useId();
  const creadoRef = useRef<HTMLHeadingElement>(null);

  const [seleccion, setSeleccion] = useState<string[]>([]);
  const [busqueda, setBusqueda] = useState("");
  const [para, setPara] = useState("");
  const [vence, setVence] = useState<VenceEnDias>(VENCE_EN_DEFAULT);
  // Sólo cuando el portapapeles falló: el enlace creado, para copiarlo a mano.
  const [creado, setCreado] = useState<ShareBundleCreateResponse | null>(null);

  // El panel «enlace creado» reemplaza el formulario dentro del mismo
  // diálogo abierto: un cambio de contenido, no una reapertura. El botón
  // que tenía el foco desaparece, así que el foco pasa al título nuevo
  // (misma postura que ActivateUnitDrawer); si no, caería al <body>, fuera
  // de la trampa de Tab del Drawer.
  useEffect(() => {
    if (creado) focusHeading(creadoRef);
  }, [creado]);

  const finding = recipe?.labels.finding ?? "Observación";
  const findingPlural = recipe?.labels.findingPlural ?? "Observaciones";
  const tienda = (recipe?.labels.asset ?? "Tienda").toLowerCase();

  const opciones = opcionesAbiertas(dashboard.data, recipe);
  const visibles = filtrarOpciones(opciones, busqueda);
  const tiendas = tiendasEnSeleccion(opciones, seleccion);
  const mezclaTiendas = tiendas > 1;
  const sobreTope = excedeTope(seleccion);
  const puedeCrear = seleccion.length > 0 && !mezclaTiendas && !sobreTope && !crear.isPending;

  function toggle(id: string) {
    setSeleccion((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function onCrear() {
    if (!puedeCrear) return;
    const expiresAt = expiresAtFrom(vence);
    const recipientLabel = para.trim();
    let res: ShareBundleCreateResponse;
    try {
      res = await crear.mutateAsync({
        findingIds: seleccion,
        expiresAt,
        ...(recipientLabel.length > 0 ? { recipientLabel } : {}),
      });
    } catch {
      // crear.error se muestra en línea; el drawer sigue abierto para
      // reintentar sin volver a elegir.
      return;
    }
    const copiado = await copiarAlPortapapeles(res.url);
    if (copiado) {
      toast.show(
        `Enlace creado y copiado · vence el ${fmtDate(res.expiresAt)}`,
        "ok",
        ADMIN_TOAST_OPTS
      );
      onClose();
      return;
    }
    setCreado(res);
  }

  const errMessage = errorCrearEnlace(crear.error, tienda);
  const paraId = `${ids}-para`;
  const paraHintId = `${ids}-para-hint`;

  if (creado) {
    return (
      <Drawer
        title="Crear enlace compartido"
        onClose={onClose}
        footer={
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onClose}
            title="Cierra este panel; el enlace ya está creado"
          >
            Listo
          </button>
        }
      >
        <h3 ref={creadoRef} tabIndex={-1} className="drawer__obs-title">
          Enlace creado
        </h3>
        <p className="ag-muted ag-mb16">
          El enlace vence el {fmtDate(creado.expiresAt)}. No pudimos copiarlo solo: cópialo desde
          aquí.
        </p>
        <CopyLinkField id={`${ids}-url`} label="Enlace" value={creado.url} />
      </Drawer>
    );
  }

  return (
    <Drawer
      title="Crear enlace compartido"
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onClose}
            title="Cierra el panel sin crear el enlace"
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!puedeCrear}
            onClick={() => void onCrear()}
            title="Crea el enlace y copia su dirección al portapapeles"
          >
            Crear y copiar enlace
          </button>
        </>
      }
    >
      <p className="ag-muted ag-mb16">
        Quien reciba el enlace ve solo lo que incluyas aquí, sin iniciar sesión. Puedes revocarlo
        cuando quieras.
      </p>

      {/* El Field «Qué incluye» del handoff vuelve aquí cuando el backend
          conozca `content_kind` (actas de tiendas, zona completa): hoy la
          única opción es una observación específica, y un select de una
          opción no es una elección. */}

      <fieldset className="store-picker" disabled={crear.isPending}>
        {/* El contador no está en el handoff: anticipa el tope de
            MAX_POR_ENLACE antes de que la alerta de abajo lo diga. */}
        <legend className="store-picker__legend">
          {findingPlural} incluidas — <span className="mono">{seleccion.length}</span> de{" "}
          <span className="mono">{MAX_POR_ENLACE}</span>
        </legend>
        {opciones.length >= SEARCH_THRESHOLD && (
          <div className="store-picker__head">
            <input
              type="search"
              className="input store-picker__search"
              placeholder="Buscar"
              aria-label={`Buscar ${finding.toLowerCase()}`}
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
        )}
        <div className="store-picker__list">
          {dashboard.isError ? (
            // El tablero no cargó: decirlo y dejar reintentar, no un
            // «no hay observaciones» que suena a verdad.
            <div className="store-picker__empty" role="alert">
              <span className="cell-meta">
                No pudimos cargar las {findingPlural.toLowerCase()}.
              </span>{" "}
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => void dashboard.refetch()}
                title={`Vuelve a pedir las ${findingPlural.toLowerCase()} abiertas`}
              >
                Reintentar
              </button>
            </div>
          ) : dashboard.isPending && dashboard.fetchStatus === "fetching" ? (
            // Filas de opción en esqueleto dentro del mismo marco, como
            // StorePickerSkel: la lista las reemplaza sin saltos. Sección
            // de un drawer ya anunciado, así que sin anuncio propio.
            <PageSkeleton
              label={`Cargando ${findingPlural.toLowerCase()}…`}
              announce={false}
              kind="section"
            >
              {SKEL_WIDTHS.map((width, i) => (
                <div key={i} className="store-picker__option">
                  <Skel width={13} height={13} />
                  <Skel variant="text" width={width} />
                </div>
              ))}
            </PageSkeleton>
          ) : opciones.length === 0 ? (
            <span className="store-picker__empty cell-meta">
              No hay {findingPlural.toLowerCase()} abiertas para compartir.
            </span>
          ) : visibles.length === 0 ? (
            <span className="store-picker__empty cell-meta">
              No hay {findingPlural.toLowerCase()} que coincidan con «{busqueda}».
            </span>
          ) : (
            visibles.map((o) => (
              <label key={o.id} className="store-picker__option">
                <input
                  type="checkbox"
                  checked={seleccion.includes(o.id)}
                  onChange={() => toggle(o.id)}
                />
                <span className="store-picker__option-label">
                  {o.titulo}
                  <span className="store-picker__option-sub">
                    {" · "}
                    {o.tienda}
                    {o.zona ? ` · ${o.zona}` : ""}
                  </span>
                </span>
              </label>
            ))
          )}
        </div>
      </fieldset>

      {mezclaTiendas && (
        <p className="auth-error" role="alert">
          Todas las observaciones deben pertenecer a la misma {tienda} para compartirlas en un solo
          enlace.
        </p>
      )}

      {sobreTope && (
        <p className="auth-error" role="alert">
          Un enlace puede incluir hasta {MAX_POR_ENLACE} {findingPlural.toLowerCase()}. Quita{" "}
          {seleccion.length - MAX_POR_ENLACE} para continuar.
        </p>
      )}

      <div className="grid-2 ag-mt16">
        <div className="field ag-field">
          <label className="field__label" htmlFor={paraId}>
            Para
          </label>
          <input
            id={paraId}
            className="input"
            type="text"
            placeholder="Ej. Directorio, contratista"
            maxLength={120}
            value={para}
            onChange={(e) => setPara(e.target.value)}
            aria-describedby={paraHintId}
          />
          <p id={paraHintId} className="field__hint">
            Solo informativo; aparece en el registro de accesos.
          </p>
        </div>
        <label className="field ag-field">
          <span className="field__label">Vence en</span>
          <select
            className="select"
            value={String(vence)}
            onChange={(e) => setVence(Number(e.target.value) as VenceEnDias)}
          >
            {VENCE_EN_DIAS.map((d) => (
              <option key={d} value={String(d)}>
                {d} días
              </option>
            ))}
          </select>
        </label>
      </div>

      {errMessage && (
        <p className="auth-error" role="alert">
          {errMessage}
        </p>
      )}
    </Drawer>
  );
}
